import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AddRounded as AddIcon,
  BarChartRounded as ChartIcon,
  DeleteOutlineRounded as DeleteIcon,
  EditRounded as EditIcon,
  InfoOutlined as InfoIcon,
  LocalGasStationRounded as FuelIcon,
  OpacityRounded as MeasurementIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Layout from "../../Layout";
import MobileBottomNavigation from "../MobileBottomNavigation";
import MobileGasoilEntryDialog from "./MobileGasoilEntryDialog";
import { selectedFilteredVessel } from "../../../selectors/filterSelector";
import { getVessels } from "../../../data/vessels";

const API_URL = process.env.REACT_APP_API_URL;
const APP_URL = process.env.REACT_APP_URL || "";

const normalizeCollection = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  return value?.["hydra:member"] || value?.member || value?.data || value?.items || [];
};

const getId = (value: any) => {
  const source = typeof value === "string" ? value : value?.["@id"] || value?.id;
  return source ? String(source).split("/").filter(Boolean).pop() : "";
};

const formatNumber = (value: any) => new Intl.NumberFormat("nl-BE", { maximumFractionDigits: 0 }).format(Number(value) || 0);
const formatDate = (value: any, withTime = false) => value && dayjs(value).isValid() ? dayjs(value).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : "—";

const userLabel = (user: any) => {
  if (!user || typeof user === "string") return "—";
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || user.email || "—";
};

type DialogState = { kind: "measurement" | "bunkering"; item?: any } | null;

const MobileGasoilPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const vessel = useSelector(selectedFilteredVessel);
  const vesselId = getId(vessel);
  const vesselIri = vesselId ? `/api/vessels/${vesselId}` : "";
  const [measurements, setMeasurements] = useState<any[]>([]);
  const [bunkerings, setBunkerings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<DialogState>(null);

  useEffect(() => {
    dispatch<any>(getVessels());
  }, [dispatch]);

  const refresh = useCallback(async () => {
    if (!vesselIri) {
      setMeasurements([]);
      setBunkerings([]);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const headers = { accept: "application/ld+json", Authorization: `Bearer ${localStorage.getItem("token")}` };
      const [measurementResponse, bunkeringResponse] = await Promise.all([
        axios.get(`${API_URL}/vessel_gasoils?vessel=${encodeURIComponent(vesselIri)}&order[date]=desc`, { headers }),
        axios.get(`${API_URL}/vessel_bunkerings?vessel=${encodeURIComponent(vesselIri)}&order[date]=desc`, { headers }),
      ]);
      setMeasurements(normalizeCollection(measurementResponse.data));
      setBunkerings(normalizeCollection(bunkeringResponse.data));
    } catch (exception: any) {
      setError(exception?.response?.data?.detail || exception?.message || t("failedToLoadGasoilDashboard", "Failed to load gasoil dashboard."));
    } finally {
      setLoading(false);
    }
  }, [vesselIri, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const currentOnboard = measurements[0] || null;
  const latestBunkering = bunkerings[0] || null;
  const supplierOptions = useMemo(
    () =>
      Array.from(
        new Set(
          bunkerings
            .map((item) => String(item?.supplier || "").trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [bunkerings]
  );

  const chartRows = useMemo(() => {
    const rows = Array.from({ length: 7 }).map((_, index) => {
      const date = dayjs().subtract(6 - index, "week").startOf("week");
      return { key: date.format("YYYY-MM-DD"), label: date.format("DD/MM"), bunkered: 0, consumed: 0 };
    });
    const rowMap = new Map(rows.map((row) => [row.key, row]));
    bunkerings.forEach((item) => {
      const row = rowMap.get(dayjs(item.date).startOf("week").format("YYYY-MM-DD"));
      if (row) row.bunkered += Number(item.amount) || 0;
    });
    const oldestFirst = [...measurements].reverse();
    for (let index = 1; index < oldestFirst.length; index += 1) {
      const previous = Number(oldestFirst[index - 1].amount) || 0;
      const current = Number(oldestFirst[index].amount) || 0;
      const row = rowMap.get(dayjs(oldestFirst[index].date).startOf("week").format("YYYY-MM-DD"));
      if (row) row.consumed += Math.max(previous - current, 0);
    }
    return rows;
  }, [measurements, bunkerings]);

  const remove = async (resource: "vessel_gasoils" | "vessel_bunkerings", item: any) => {
    if (!window.confirm(t("confirmDeleteItem", "Delete this item?"))) return;
    const url = item?.["@id"] ? `${APP_URL}${item["@id"]}` : `${API_URL}/${resource}/${getId(item)}`;
    await axios.delete(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
    await refresh();
  };

  return (
    <Layout mobileTitle={t("gasoil", "Gasoil")} mobileSubtitle={vessel?.name || "—"} mobileAction={<ChartIcon />} hideMobileVesselButton>
      <Box className="mobile-gasoil-page">
        {loading && !measurements.length && !bunkerings.length ? (
          <Box className="mobile-gasoil-loading"><CircularProgress /></Box>
        ) : null}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {!vesselIri && <Alert severity="info">{t("selectVesselToViewGasoil", "Select a vessel to view gasoil and bunkering.")}</Alert>}

        <Box className="mobile-gasoil-stats">
          <article>
            <header><strong>{t("currentOnboard", "Current onboard")}</strong><span className="is-blue"><MeasurementIcon /></span></header>
            <Typography>{formatNumber(currentOnboard?.amount)} L</Typography>
            <small>{t("asOf", "As of")} {formatDate(currentOnboard?.date, true)}</small>
          </article>
          <article>
            <header><strong>{t("lastBunkering", "Last bunkering")}</strong><span className="is-green"><FuelIcon /></span></header>
            <Typography className="is-green-text">+{formatNumber(latestBunkering?.amount)} L</Typography>
            <small>{formatDate(latestBunkering?.date)}</small>
            <b>{latestBunkering?.supplier || "—"}</b>
          </article>
        </Box>

        <section className="mobile-gasoil-card mobile-gasoil-chart-card">
          <header><strong>{t("weeklyConsumption", "Weekly consumption")}</strong><span>{t("lastEightWeeks", "Last 8 weeks")}</span></header>
          <Box className="mobile-gasoil-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="bunkered" name={t("bunkered", "Bunkered")} fill="#169447" radius={[3, 3, 0, 0]} />
                <Bar dataKey="consumed" name={t("consumed", "Consumed")} fill="#0b2e78" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </section>

        <section className="mobile-gasoil-card">
          <header><strong>{t("gasoilMeasurements", "Gasoil measurements")}</strong><InfoIcon /></header>
          <div className="mobile-gasoil-table-head"><span>{t("dateTime", "Date and time")}</span><span>{t("amountLitersShort", "Amount (L)")}</span><span>{t("addedBy", "Added by")}</span><span /></div>
          <div className="mobile-gasoil-table">
            {measurements.map((item) => (
              <div key={item["@id"] || item.id}>
                <span>{formatDate(item.date, true)}</span><strong>{formatNumber(item.amount)}</strong><span>{userLabel(item.user)}</span>
                <span className="mobile-row-actions"><IconButton onClick={() => setDialog({ kind: "measurement", item })}><EditIcon /></IconButton><IconButton onClick={() => remove("vessel_gasoils", item)}><DeleteIcon /></IconButton></span>
              </div>
            ))}
          </div>
          <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ kind: "measurement" })}>{t("addMeasurement", "Add measurement")}</Button>
        </section>

        <section className="mobile-gasoil-card">
          <header><strong>{t("bunkering", "Bunkering")}</strong></header>
          <div className="mobile-gasoil-table-head is-bunkering"><span>{t("date", "Date")}</span><span>{t("supplier", "Supplier")}</span><span>{t("amountLitersShort", "Amount (L)")}</span><span /></div>
          <div className="mobile-gasoil-table is-bunkering">
            {bunkerings.map((item) => (
              <div key={item["@id"] || item.id}>
                <span>{formatDate(item.date)}</span><span>{item.supplier || "—"}</span><strong>{formatNumber(item.amount)}</strong>
                <span className="mobile-row-actions"><IconButton onClick={() => setDialog({ kind: "bunkering", item })}><EditIcon /></IconButton><IconButton onClick={() => remove("vessel_bunkerings", item)}><DeleteIcon /></IconButton></span>
              </div>
            ))}
          </div>
          <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ kind: "bunkering" })}>{t("addBunkering", "Add bunkering")}</Button>
        </section>
      </Box>

      <MobileBottomNavigation active="gasoil" onAdd={() => setDialog({ kind: "measurement" })} />

      <MobileGasoilEntryDialog
        open={Boolean(dialog)}
        kind={dialog?.kind || "measurement"}
        vesselId={vesselId}
        initialData={dialog?.item}
        supplierOptions={supplierOptions}
        onClose={() => setDialog(null)}
        onSaved={refresh}
      />
    </Layout>
  );
};

export default MobileGasoilPage;
